import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCheckbox, IonChip, IonInput, IonItem,
  IonNote, IonSelect, IonSelectOption, IonTextarea,
} from '@ionic/react';
import {
  LaborLineInput, MaterialLineInput, ServiceCustomer, ServiceEstimate, ServiceEstimateWrite, ServiceSettingsBundle,
} from '../../../interfaces/ServiceEstimate';
import { calcServiceEstimate, ratesFromSettings } from '../../../utils/calcServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import CustomerPicker from './CustomerPicker';
import { STATUS_COLOR, STATUS_LABEL, money, pct, reviewReasons, shortDate, todayIso } from './serviceFormat';
import './EstimateEditor.css';

interface Props {
  bundle: ServiceSettingsBundle;
  estimate: ServiceEstimate | null;
  onSaved: (e: ServiceEstimate) => void;
  onNew: () => void;
  notify: (msg: string) => void;
  /** Tells the page whether there are unsaved edits (the Proposal tab shows the saved copy). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Switch to the Proposal tab. */
  onOpenProposal?: () => void;
}

interface Draft {
  customer: ServiceCustomer | null;
  preparedBy: number | null;
  contactName: string;
  contactEmail: string;
  date: string;
  scope: string;
  assumptions: string[];
  location: string; // tax location id, 'exempt', or ''
  locationAuto: boolean;
  freightOn: boolean;
  freightPct: string;
  discount: string;
  materials: MaterialLineInput[];
  labor: LaborLineInput[];
}

const blankMaterial = (): MaterialLineInput => ({ description: '', alpha_cost: '' });
const blankLabor = (): LaborLineInput => ({ description: '', hours: '', labor_type: 'ME-Preferred' });

const fromEstimate = (e: ServiceEstimate): Draft => ({
  customer: e.customer_detail,
  preparedBy: e.prepared_by,
  contactName: e.contact_name,
  contactEmail: e.contact_email,
  date: e.estimate_date,
  scope: e.scope_of_work,
  assumptions: e.assumptions,
  location: e.tax_exempt ? 'exempt' : e.tax_location ? String(e.tax_location) : '',
  locationAuto: false,
  freightOn: e.freight_on,
  freightPct: String(Math.round(Number(e.freight_rate) * 1e6) / 1e4),
  discount: Number(e.discount) ? String(Number(e.discount)) : '',
  materials: e.material_lines.length ? e.material_lines.map((m) => ({ description: m.description, alpha_cost: String(Number(m.alpha_cost)) })) : [blankMaterial()],
  labor: e.labor_lines.length ? e.labor_lines.map((l) => ({ description: l.description, hours: String(Number(l.hours)), labor_type: l.labor_type })) : [blankLabor()],
});

const blankDraft = (b: ServiceSettingsBundle): Draft => ({
  customer: null, preparedBy: b.user_id ?? null, contactName: '', contactEmail: '', date: todayIso(), scope: '',
  assumptions: [b.settings.default_assumption], location: '', locationAuto: false, freightOn: true,
  freightPct: String(Math.round(Number(b.settings.default_freight_rate) * 1e6) / 1e4), discount: '',
  materials: [blankMaterial()], labor: [blankLabor()],
});

const writeOf = (d: Draft): ServiceEstimateWrite => ({
  customer: d.customer ? d.customer.id : '',
  prepared_by: d.preparedBy ?? 0,
  contact_name: d.contactName, contact_email: d.contactEmail, estimate_date: d.date, scope_of_work: d.scope,
  assumptions: d.assumptions.filter((a) => a.trim()),
  tax_location: d.location && d.location !== 'exempt' ? Number(d.location) : null, tax_exempt: d.location === 'exempt',
  freight_on: d.freightOn, freight_rate: String((Number(d.freightPct) || 0) / 100), discount: String(Number(d.discount) || 0),
  materials: d.materials.filter((m) => m.description.trim() || Number(m.alpha_cost)).map((m) => ({ description: m.description, alpha_cost: String(Number(m.alpha_cost) || 0) })),
  labor: d.labor.filter((l) => l.description.trim() || Number(l.hours)).map((l) => ({ description: l.description, hours: String(Number(l.hours) || 0), labor_type: l.labor_type })),
});

const EstimateEditor: React.FC<Props> = ({ bundle, estimate, onSaved, onNew, notify, onDirtyChange, onOpenProposal }) => {
  const [d, setD] = useState<Draft>(() => (estimate ? fromEstimate(estimate) : blankDraft(bundle)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setD(estimate ? fromEstimate(estimate) : blankDraft(bundle));
    setError('');
  }, [estimate, bundle]);

  const status = estimate?.status ?? 'draft';
  const locked = status === 'accepted' || status === 'declined';
  const hasAcs = !!estimate?.acs_number;
  const rates = useMemo(() => ratesFromSettings(bundle), [bundle]);
  const locations = bundle.tax_locations;
  const taxRate = d.location === 'exempt' || !d.location ? 0 : Number(locations.find((l) => String(l.id) === d.location)?.rate || 0);
  const c = calcServiceEstimate({
    materials: d.materials.map((m) => ({ description: m.description, alphaCost: Number(m.alpha_cost) || 0 })),
    labor: d.labor.map((l) => ({ description: l.description, hours: Number(l.hours) || 0, laborType: l.labor_type })),
    taxRate, freightOn: d.freightOn, freightRate: (Number(d.freightPct) || 0) / 100, discount: Number(d.discount) || 0,
  }, rates);
  const reasons = reviewReasons(c.totalPrice, c.margin, bundle.settings);

  // Unsaved edits: compare with the saved estimate (or a blank one).
  const baseline = useMemo(() => JSON.stringify(writeOf(estimate ? fromEstimate(estimate) : blankDraft(bundle))), [estimate, bundle]);
  const dirty = !locked && JSON.stringify(writeOf(d)) !== baseline;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const set = (p: Partial<Draft>) => setD((prev) => ({ ...prev, ...p }));
  const usual = d.customer ? (d.customer.tax_exempt ? 'exempt' : d.customer.tax_location ? String(d.customer.tax_location) : '') : '';

  const chooseCustomer = (cust: ServiceCustomer | null) => {
    if (!cust) {
      set({ customer: null, ...(d.locationAuto ? { location: '', locationAuto: false } : {}) });
      return;
    }
    const auto = cust.tax_exempt ? 'exempt' : cust.tax_location ? String(cust.tax_location) : '';
    set({
      customer: cust,
      contactName: d.contactName || cust.contact_name,
      contactEmail: d.contactEmail || cust.contact_email,
      ...(auto && (!d.location || d.locationAuto) ? { location: auto, locationAuto: true } : {}),
    });
  };

  let locNote = '';
  let locClass = '';
  if (d.locationAuto && d.customer) { locNote = `Applied automatically from ${d.customer.name}'s customer record. Change it only if this job is at a different site.`; locClass = 'good'; }
  else if (d.customer && usual && d.location && d.location !== usual) { locNote = `This differs from ${d.customer.name}'s usual location (${d.customer.tax_location_label}). The office will check it before assigning the ACS#.`; locClass = 'warn'; }
  else if (d.customer && !usual) locNote = "New customer: pick where the work is done. It's saved to the customer record, so next time it fills in by itself.";
  else if (!d.customer) locNote = "Pick the customer first; a known customer's location fills in automatically.";
  else if (d.location === 'exempt') locNote = "No sales tax. Keep the customer's exemption certificate on file.";
  if (!locations.length) { locNote = 'No tax locations are set up yet. Admin or Director adds them on the Office tab.'; locClass = 'warn'; }

  const missing = (): string[] => {
    const m: string[] = [];
    if (!d.customer) m.push('customer');
    if (!d.preparedBy) m.push('technician');
    if (!d.location) m.push('job location');
    if (!d.scope.trim()) m.push('scope of work');
    if (!c.totalPrice) m.push('material or labor');
    return m;
  };

  /** Saves the draft; returns the saved estimate, or null after showing the error. */
  const persist = async (): Promise<ServiceEstimate | null> => {
    if (!d.customer) { setError('Pick or add the customer first.'); return null; }
    if (!d.preparedBy) { setError('Pick the technician who prepared the estimate.'); return null; }
    setError('');
    try {
      return estimate ? await serviceApi.updateEstimate(estimate.id, writeOf(d)) : await serviceApi.createEstimate(writeOf(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save. Try again.');
      return null;
    }
  };

  const save = async () => {
    setBusy(true);
    const wasIssued = status === 'ready' || status === 'sent';
    const saved = await persist();
    setBusy(false);
    if (!saved) return;
    onSaved(saved);
    if (wasIssued && saved.status === 'submitted') notify(`Saved and sent back to the office to recheck ${saved.acs_number}.`);
    else notify(`Saved for ${saved.customer_detail.name}`);
  };

  const submit = async () => {
    const m = missing();
    if (m.length) { setError(`Add the ${m.join(', ')} before submitting.`); return; }
    setBusy(true);
    try {
      let saved: ServiceEstimate | null = estimate;
      if (!estimate || dirty) saved = await persist();
      if (!saved) return;
      if (saved.status === 'draft') saved = await serviceApi.submit(saved.id);
      onSaved(saved);
      notify(saved.review_reasons.length ? 'Submitted. The office will review it and assign the ACS#.'
        : saved.acs_number ? `Resubmitted. The office will recheck ${saved.acs_number}.` : 'Submitted. The office will assign the ACS#.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const expired = !!estimate?.is_expired;
  const statusKey = expired ? 'expired' : status;
  let statusNote = '';
  let submitLabel = '';
  if (status === 'accepted' && estimate) statusNote = `Accepted by ${estimate.accepted_name || 'the customer'} on ${shortDate(estimate.accepted_at)}.`;
  else if (status === 'declined' && estimate) statusNote = `Declined on ${shortDate(estimate.declined_at)}.`;
  else if (status === 'draft') {
    statusNote = hasAcs ? `Make the changes the office asked for, then resubmit. It keeps ${estimate?.acs_number}.`
      : 'Submit to ask the office for an ACS#. The proposal can be shown on screen but not sent or signed until it has one.';
    submitLabel = hasAcs ? 'Resubmit to office' : 'Submit for ACS#';
  } else if (status === 'submitted') {
    statusNote = estimate?.changed_after_acs
      ? `Changed after ${estimate.acs_number} was assigned. Waiting for the office to ${estimate.review_reasons.length ? 'review and ' : ''}recheck it.`
      : `Waiting for the office to ${estimate?.review_reasons.length ? 'review it and ' : ''}assign the ACS#.`;
  } else if (dirty) {
    statusNote = `You changed this estimate after ${estimate?.acs_number} was assigned. Saving sends it back to the office to recheck it; it keeps the same ACS#.`;
    submitLabel = 'Resubmit to office';
  } else if (expired && estimate) statusNote = `Expired on ${shortDate(estimate.valid_until)}. Change the date and resubmit to re-issue it with the same ACS#.`;
  else if (estimate) statusNote = `Ready to send. Valid until ${shortDate(estimate.valid_until)}. Open the Proposal tab for the PDF or the customer's signature.`;
  if (!estimate && !submitLabel) submitLabel = 'Submit for ACS#';

  const techs = bundle.technicians || [];
  const showReview = !locked && reasons.length > 0;

  return (
    <div className="estimate-editor">
      {locked && estimate && (
        <div className={`estimate-banner ${estimate.status === 'declined' ? 'bad' : 'good'}`}>
          {estimate.status === 'accepted'
            ? `Signed and accepted by ${estimate.accepted_name} on ${shortDate(estimate.accepted_at)}. The estimate is locked; start a new estimate for any change.`
            : 'The customer declined this proposal. The estimate is locked; start a new estimate to re-quote.'}
        </div>
      )}

      <IonCard>
        <IonCardHeader className="estimate-status-head">
          <IonCardTitle>Status</IonCardTitle>
          <IonChip color={STATUS_COLOR[statusKey]}>{STATUS_LABEL[statusKey]}</IonChip>
          {estimate?.acs_number && <strong className="estimate-acs">{estimate.acs_number}</strong>}
        </IonCardHeader>
        <IonCardContent>
          {showReview && (
            <div className="estimate-flag"><strong>Needs office review.</strong> {reasons.join(' ')} Admin or Director approves it before the ACS# is assigned.</div>
          )}
          {status === 'draft' && estimate?.office_note && <div className="estimate-flag bad">Sent back by the office: {estimate.office_note}</div>}
          <p className="estimate-note">{statusNote}</p>
          <div className="estimate-actions">
            {submitLabel && <IonButton onClick={submit} disabled={busy || locked}>{submitLabel}</IonButton>}
            <IonButton fill={submitLabel ? 'outline' : 'solid'} onClick={save} disabled={busy || locked || (!!estimate && !dirty)}>Save</IonButton>
            {(status === 'ready' || status === 'sent' || locked) && !dirty && onOpenProposal && (
              <IonButton fill="outline" onClick={onOpenProposal}>Open proposal</IonButton>
            )}
            <IonButton fill="outline" onClick={onNew}>New estimate</IonButton>
          </div>
          {error && <p className="estimate-error">{error}</p>}
        </IonCardContent>
      </IonCard>

      <fieldset className="estimate-fields" disabled={locked}>
        <IonCard>
          <IonCardHeader><IonCardTitle>Customer</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <CustomerPicker value={d.customer} disabled={locked} onChange={chooseCustomer} />
            <div className="estimate-grid">
              <IonItem><IonInput label="Customer contact" labelPlacement="stacked" placeholder="Name of person approving" value={d.contactName} onIonInput={(e) => set({ contactName: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Contact email" labelPlacement="stacked" type="email" value={d.contactEmail} onIonInput={(e) => set({ contactEmail: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Date of estimate" labelPlacement="stacked" type="date" value={d.date} onIonInput={(e) => set({ date: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem>
                <IonSelect label="Prepared by" labelPlacement="stacked" placeholder="Select technician…" value={d.preparedBy} interface="popover"
                  onIonChange={(e) => set({ preparedBy: e.detail.value ? Number(e.detail.value) : null })}>
                  {techs.map((t) => <IonSelectOption key={t.id} value={t.id} className='text-dark-blue'>{t.name}</IonSelectOption>)}
                  {estimate && !techs.some((t) => t.id === estimate.prepared_by) && (
                    <IonSelectOption value={estimate.prepared_by}>{estimate.prepared_by_name}</IonSelectOption>
                  )}
                </IonSelect>
              </IonItem>
            </div>
            <div className="estimate-location">
              <IonItem className="estimate-location-pick">
                <IonSelect label="Job location for sales tax" labelPlacement="stacked" placeholder="Select the job location…" value={d.location} interface="popover"
                  onIonChange={(e) => set({ location: String(e.detail.value ?? ''), locationAuto: false })}>
                  {locations.map((l) => <IonSelectOption key={l.id} value={String(l.id)}>{`${l.label} — ${(Number(l.rate) * 100).toFixed(2)}%`}</IonSelectOption>)}
                  <IonSelectOption value="exempt">Tax exempt (certificate on file) — 0.00%</IonSelectOption>
                </IonSelect>
              </IonItem>
              <IonChip className="estimate-taxrate" outline>Tax rate {(taxRate * 100).toFixed(2)}%</IonChip>
            </div>
            <p className={`estimate-note ${locClass}`}>{locNote}</p>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Scope of work</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p className="estimate-help">Description of work that will appear on the proposal. Include any info that will explain the scope to the customer that will eliminate any follow up questions.</p>
            <IonTextarea autoGrow rows={5} fill="outline" value={d.scope} onIonInput={(e) => set({ scope: String(e.detail.value ?? '') })}
              placeholder="e.g. Furnish and install new 1/2 HP condenser fan motor on RTU-3. Recover refrigerant as required, verify operation and amp draw on start-up." />
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Material</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {d.materials.map((m, i) => (
              <div className="estimate-line" key={i}>
                <div className="estimate-line-fields material">
                  <IonItem><IonInput label="Material description" labelPlacement="stacked" placeholder="e.g. Condenser fan motor 1/2 HP" value={m.description}
                    onIonInput={(e) => set({ materials: d.materials.map((x, j) => (j === i ? { ...x, description: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem><IonInput label="Alpha cost ($)" labelPlacement="stacked" inputmode="decimal" placeholder="0.00" value={m.alpha_cost}
                    onIonInput={(e) => set({ materials: d.materials.map((x, j) => (j === i ? { ...x, alpha_cost: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                </div>
                <IonButton fill="clear" color="medium" aria-label="Remove material line"
                  onClick={() => set({ materials: d.materials.length > 1 ? d.materials.filter((_, j) => j !== i) : [blankMaterial()] })}>×</IonButton>
                <div className="estimate-calc">
                  <span>Markup <b>{c.materialLines[i]?.alphaCost ? pct(c.materialLines[i].markupRate) : '—'}</b></span>
                  <span>Marked up <b>{money(c.materialLines[i]?.markedUp)}</b></span>
                  <span>After tax <b>{money(c.materialLines[i]?.afterTax)}</b></span>
                </div>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ materials: [...d.materials, blankMaterial()] })}>+ Add material line</IonButton>
            <p className="estimate-help">Markup by each line&apos;s Alpha cost: under $101 → 100%, $101–300 → 80%, $301–1,000 → 60%, $1,001–2,000 → 40%, $2,001 and up → 25%. Tax applies to material only.</p>
            <div className="estimate-subtotal"><span>Total material (marked up, after tax)</span><span>{money(c.materialTotal)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Labor</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {d.labor.map((l, i) => (
              <div className="estimate-line" key={i}>
                <div className="estimate-line-fields labor">
                  <IonItem><IonInput label="Labor description" labelPlacement="stacked" placeholder="e.g. Service technician" value={l.description}
                    onIonInput={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, description: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem><IonInput label="Hours" labelPlacement="stacked" inputmode="decimal" placeholder="0" value={l.hours}
                    onIonInput={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, hours: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem>
                    <IonSelect label="Labor type" labelPlacement="stacked" interface="popover"  value={l.labor_type}
                      onIonChange={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, labor_type: String(e.detail.value) } : x)) })}>
                      {bundle.labor_types.map((t) => <IonSelectOption key={t.code} value={t.code}>{t.code}</IonSelectOption>)}
                    </IonSelect>
                  </IonItem>
                </div>
                <IonButton fill="clear" color="medium" aria-label="Remove labor line"
                  onClick={() => set({ labor: d.labor.length > 1 ? d.labor.filter((_, j) => j !== i) : [blankLabor()] })}>×</IonButton>
                <div className="estimate-calc">
                  <span>Rate <b>{money(c.laborLines[i]?.rate)}/hr</b></span>
                  <span>Line total <b>{money(c.laborLines[i]?.lineTotal)}</b></span>
                </div>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ labor: [...d.labor, blankLabor()] })}>+ Add labor line</IonButton>
            <div className="estimate-subtotal"><span>Total labor · {c.laborHours} hr</span><span>{money(c.laborTotal)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Freight and discount</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <IonItem lines="none">
              <IonCheckbox checked={d.freightOn} onIonChange={(e) => set({ freightOn: e.detail.checked })} labelPlacement="end">Freight (if required) · % of total material after tax</IonCheckbox>
            </IonItem>
            <div className="estimate-grid">
              <IonItem><IonInput label="Freight %" labelPlacement="stacked" inputmode="decimal" value={d.freightPct} disabled={!d.freightOn} onIonInput={(e) => set({ freightPct: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Customer discount ($)" labelPlacement="stacked" inputmode="decimal" placeholder="0.00" value={d.discount} onIonInput={(e) => set({ discount: String(e.detail.value ?? '') })} /></IonItem>
            </div>
            <div className="estimate-subtotal"><span>Freight</span><span>{money(c.freight)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Assumptions</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p className="estimate-help">Information not included in scope but important, like lead time or exclusions.</p>
            {d.assumptions.map((a, i) => (
              <div className="estimate-assumption" key={i}>
                <span aria-hidden="true">•</span>
                <IonTextarea autoGrow rows={1} fill="outline" value={a} aria-label="Assumption" placeholder="e.g. Lead time on motor is 2–3 weeks from approval."
                  onIonInput={(e) => set({ assumptions: d.assumptions.map((x, j) => (j === i ? String(e.detail.value ?? '') : x)) })} />
                <IonButton fill="clear" color="medium" aria-label="Remove assumption" onClick={() => set({ assumptions: d.assumptions.filter((_, j) => j !== i) })}>×</IonButton>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ assumptions: [...d.assumptions, ''] })}>+ Add assumption</IonButton>
          </IonCardContent>
        </IonCard>
      </fieldset>

      <IonCard className="estimate-margin">
        <IonCardHeader className="estimate-status-head">
          <IonCardTitle>Margin check</IonCardTitle><IonChip color="warning">Internal · not on proposal</IonChip>
        </IonCardHeader>
        <IonCardContent className="estimate-kpis">
          <div><IonNote>Total price</IonNote><strong>{money(c.totalPrice)}</strong><IonNote>rounded up to $5</IonNote></div>
          <div><IonNote>Labor cost</IonNote><strong>{money(c.internalLaborCost)}</strong><IonNote>{c.laborHours} hr × {money(rates.internalLaborCost)}</IonNote></div>
          <div><IonNote>Material cost</IonNote><strong>{money(c.internalMaterialCost)}</strong><IonNote>Alpha cost + freight, taxed</IonNote></div>
          <div><IonNote>Project margin</IonNote><strong className={c.margin !== null && c.margin < Number(bundle.settings.review_margin) ? 'estimate-low' : ''}>{pct(c.margin)}</strong><IonNote>on the rounded total</IonNote></div>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default EstimateEditor;
